import React, { useEffect, useState, useCallback } from "react";
import { Header } from "../header";
import { useParams } from "react-router-dom";
import TableCart from "./TableCart/TableCart";
import { useTranslation } from "react-i18next";
import {
  calculateTotal,
  getCartItems,
  removeFromCart,
  updateQuantity,
  updateCartPrices,
} from "../utils/CartFunctions";
import { ShoppingCart } from "lucide-react";
import Visibility from "../utils/Visibility";
import { indexStore } from "../../store/indexStore";
import { BrowseProducts, CheckoutComponent } from "./Tools";
import IsNotActive from "../IsNotActive/IsNotActive";
import ProductModel from "../../Models/PorductModel";
import menuServices from "../../api-service/menu-service";

// ============================================================================
// Cart Component
// ============================================================================

function Cart() {
  const params = useParams();
  const { fetchBrancheAction, branch } = indexStore();
  const { t } = useTranslation();
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);

  // تحميل العربة وتحديث الإجمالي
  const loadCart = useCallback(() => {
    const branchId = Number(params.branch_id);
    if (!branchId) return;
    
    const items = getCartItems(branchId);
    setCartItems(items);
    setTotal(calculateTotal(branchId));
  }, [params.branch_id]);

  // التحقق من الأسعار وتحديثها بعد تحميل السلة
  const checkAndUpdatePrices = useCallback(async () => {
    const branchId = Number(params.branch_id);
    if (!branchId) return;

    const items = getCartItems(branchId);
    if (items.length === 0) return;

    // تحديث الأسعار من API
    await updateCartPrices(branchId, menuServices.selectedProduct);
    
    // إعادة تحميل السلة بعد التحديث
    loadCart();
  }, [params.branch_id, loadCart]);

  useEffect(() => {
    if (!branch || Object.keys(branch).length === 0) {
      fetchBrancheAction({ branch_id: params.branch_id });
    }
    loadCart();
    // التحقق من الأسعار بعد تحميل السلة
    checkAndUpdatePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // تحديث الكمية
  const handleUpdateQuantity = useCallback((product: ProductModel, newQuantity: number) => {
    const branchId = Number(params.branch_id);
    if (!branchId) return;
    
    updateQuantity(product, newQuantity, branchId);
    loadCart();
  }, [params.branch_id, loadCart]);

  // حذف عنصر
  const handleRemoveItem = useCallback((product: ProductModel) => {
    const branchId = Number(params.branch_id);
    if (!branchId) return;
    
    removeFromCart(product, branchId);
    loadCart();
  }, [params.branch_id, loadCart]);

  return (
    <IsNotActive>
      <div className="flex flex-col flex-grow bg-background min-h-screen">
        <title>{params?.domain?.toUpperCase() || "Cart"}</title>
        
        <Visibility visible={cartItems.length === 0}>
          <EmptyCart params={params} t={t} />
        </Visibility>

        <Visibility visible={cartItems.length > 0}>
          <div className="flex flex-col pb-24 md:pb-28">
            <div className="bg-primary rounded-b-3xl p-4 md:p-6 pb-6 md:pb-8 shadow-lg opacity-0 animate-fadeIn">
              <Header />
              <div className="flex flex-row justify-between items-center gap-3 ">
                <h3 className="text-2xl md:text-3xl font-bold text-onPrimary">
                  {t("gloabl.Cart")}
                </h3>
                <BrowseProducts
                  params={params}
                  t={t}
                  className="!bg-onPrimary !text-primary hover:!bg-onPrimary/90 shadow-md"
                />
              </div>
            </div>

            {/* Content Section */}
            <div className="px-4 md:px-6 flex flex-col gap-5 md:gap-6 mt-4 md:mt-6 opacity-0 animate-[fadeIn_0.3s_ease-out_0.2s_forwards]">
              {/* Cart Items */}
              <TableCart
                data={cartItems}
                handleUpdateQuantity={handleUpdateQuantity}
                handleRemoveItem={handleRemoveItem}
              />

              {/* Checkout Section */}
              {/* <CheckoutComponent
                cartItems={cartItems}
                total={total}
                t={t}
                params={params}
                loadCart={loadCart}
              /> */}
            </div>
          </div>
        </Visibility>
      </div>
    </IsNotActive>
  );
}

export default Cart;

// ============================================================================
// EmptyCart Component
// ============================================================================

const EmptyCart = ({ params, t }: { params: any; t: any }) => {
  return (
    <div className="flex flex-col gap-5 md:gap-6 items-center justify-center min-h-[70vh] text-center p-6 md:p-8 opacity-0 animate-fadeIn">
      <div className="p-5 md:p-6 rounded-full bg-gradient-to-br from-muted/50 to-muted/30 border-2 border-border/50">
        <ShoppingCart className="w-20 h-20 md:w-24 md:h-24 text-muted-foreground" />
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">
          {t("gloabl.emptyCart")}
        </h2>
        <p className="text-sm md:text-base text-muted-foreground max-w-md mx-auto">
          {t("gloabl.emptyCartMessage")}
        </p>
      </div>
      <div className="mt-4">
        <BrowseProducts 
          params={params} 
          t={t}
          className="shadow-lg hover:shadow-xl"
        />
      </div>
    </div>
  );
};
